import React from 'react';

import { Box, Grid, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';

import { FEIN_MASK, INDUSTRIES_OPTIONS } from '@clearverify/constants';
import type { SimpleObject } from '@clearverify/types';

import { VerifierTypeOfGovernment } from 'src/api';
import { Button } from 'src/components/common';
import {
  AddressFields,
  AutocompleteField,
  Field,
  Form,
  MaskedTextField,
  PhoneField,
  SelectField,
  TextField,
} from 'src/components/finalForm';
import { SignupVerifierFormStepId } from 'src/constants/auth';
import { useAuthSignupVerifier } from 'src/hooks/auth';
import { enumToSelectOptions } from 'src/utils/common';
import { validateWithYupSchema } from 'src/utils/yup';

import {
  CommercialVerifierBasicInfoFormFields,
  GovernmentVerifierBasicInfoFormFields,
  InitialStepFormFields,
  VerifierAccountTypeFormFields,
  VerifierBasicInfoFormFields,
  VerifierBasicInfoFormValues,
} from '../types';
import { verifierBasicInfoFormValidationSchema } from '../utils';

export const VerifierBasicInfoVerifierAdminForm: React.FC = () => {
  const { formData, updateFormData, goToStep } = useAuthSignupVerifier();

  const initialValues = React.useRef({
    [VerifierBasicInfoFormFields.accountType]: formData[VerifierAccountTypeFormFields.accountType],
  });

  const handleSubmit = React.useCallback(
    (formValues: VerifierBasicInfoFormValues) => {
      updateFormData(formValues);
      goToStep(SignupVerifierFormStepId.noticeToFurnishersVerifier);
    },
    [updateFormData, goToStep],
  );

  return (
    <React.Fragment>
      <Typography variant="h5">Basic info</Typography>
      <Typography variant="body1" color="text.secondary" mt={1}>
        Hello <b>{getUserName(formData)}</b>! Let us know more information about your company.
      </Typography>
      <Form<VerifierBasicInfoFormValues>
        initialValues={initialValues.current as SimpleObject}
        onSubmit={handleSubmit}
        subscription={{ valid: true, values: true, submitting: true, errors: true }}
        containerProps={{ mt: 4 }}
        validate={validateWithYupSchema(verifierBasicInfoFormValidationSchema)}
      >
        {({ submitting, valid, values }) => {
          const isCommercialAccount = values.accountType === 'Commercial';

          return (
            <React.Fragment>
              <Grid container spacing={1.5}>
                <Grid size={12}>
                  <Box>
                    <Field
                      component={TextField}
                      name={VerifierBasicInfoFormFields.companyDisplayInfo}
                      label="Company Display Name"
                      fullWidth
                      disabled={submitting}
                      required
                    />
                  </Box>

                  <Box mt={1.5}>
                    {isCommercialAccount ? (
                      <Field
                        component={TextField}
                        name={CommercialVerifierBasicInfoFormFields.registeredBusinessName}
                        label="Registered Business Name"
                        fullWidth
                        disabled={submitting}
                        required
                      />
                    ) : (
                      <Field
                        component={TextField}
                        name={GovernmentVerifierBasicInfoFormFields.governmentEntityName}
                        label="Government Entity Name"
                        fullWidth
                        disabled={submitting}
                        required
                      />
                    )}
                  </Box>
                </Grid>

                {!isCommercialAccount && (
                  <React.Fragment>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                      }}
                    >
                      <Field
                        component={SelectField}
                        name={GovernmentVerifierBasicInfoFormFields.typeOfGovernment}
                        label="Type of Government"
                        options={enumToSelectOptions(VerifierTypeOfGovernment)}
                        fullWidth
                        disabled={submitting}
                        required
                      />
                    </Grid>

                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                      }}
                    >
                      <Field
                        component={TextField}
                        name={GovernmentVerifierBasicInfoFormFields.governmentDepartment}
                        label="Department"
                        fullWidth
                        disabled={submitting}
                        required
                      />
                    </Grid>
                  </React.Fragment>
                )}

                <Grid size={12}>
                  <Field
                    component={TextField}
                    name={VerifierBasicInfoFormFields.primaryContactEmail}
                    label="Primary Contact Email"
                    fullWidth
                    disabled={submitting}
                    required
                  />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Field
                    component={PhoneField}
                    name={VerifierBasicInfoFormFields.primaryContactPhone}
                    label="Primary Contact Phone"
                    fullWidth
                    disabled={submitting}
                    required
                  />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Field
                    component={MaskedTextField}
                    name={VerifierBasicInfoFormFields.fein}
                    label="FEIN"
                    mask={FEIN_MASK}
                    fullWidth
                    disabled={submitting}
                    required
                  />
                </Grid>

                <Grid size={12}>
                  <Field
                    component={TextField}
                    name={VerifierBasicInfoFormFields.commercialName}
                    label="Commercial Name (optional)"
                    fullWidth
                    disabled={submitting}
                  />
                </Grid>

                <Grid size={12}>
                  <Field
                    component={AutocompleteField}
                    name={VerifierBasicInfoFormFields.industry}
                    label="Industry"
                    options={INDUSTRIES_OPTIONS}
                    fullWidth
                    disabled={submitting}
                    required
                  />
                </Grid>
              </Grid>
              <Box mt={1.5}>
                <AddressFields disabled={submitting} required />
              </Box>
              <Box mt={1.5}>
                <Field
                  component={TextField}
                  name={VerifierBasicInfoFormFields.websiteUrl}
                  label="Website URL"
                  fullWidth
                  disabled={submitting}
                  required
                />
              </Box>
              <Box sx={visuallyHidden}>
                <Field component={TextField} name={VerifierBasicInfoFormFields.accountType} />
              </Box>
              <Box display="flex" justifyContent="center" mt={4}>
                <Button
                  type="submit"
                  disabled={submitting || !valid}
                  loading={submitting}
                  size="large"
                  variant="contained"
                >
                  Next
                </Button>
              </Box>
            </React.Fragment>
          );
        }}
      </Form>
    </React.Fragment>
  );
};

const getUserName = (formData: SimpleObject) => {
  return `${formData[InitialStepFormFields.firstName]} ${formData[InitialStepFormFields.lastName]}`;
};
